// Save downloaded job paperwork straight into a folder on disk instead of
// dropping it in the browser's Downloads.
//
// A web page cannot write to an arbitrary path — that's a browser security
// boundary, not something we can code around. What it CAN do is ask the
// owner to grant one folder once (File System Access API), keep the handle,
// and write into it from then on. So the owner points us at
// ...\OneDrive\Orion\Malca once and we create 2026\September 2026 under it
// ourselves.
//
// Chrome and Edge only. Everywhere else — and any time permission is gone or
// refused — callers fall back to a normal download, which is why every entry
// point here returns a boolean rather than throwing.
//
// The handle can't go in localStorage (it isn't JSON), so it lives in
// IndexedDB, which stores it structured-clone style.

const DB_NAME = "veloceflow_fs";
const STORE = "handles";
const KEY = "saveRoot";

export function isFolderSaveSupported() {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDel(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Ask the owner to choose the root folder. Returns its name, or null. */
export async function pickSaveFolder() {
  if (!isFolderSaveSupported()) return null;
  try {
    const handle = await window.showDirectoryPicker({ id: "veloceflow-jobs", mode: "readwrite" });
    await idbSet(KEY, handle);
    return handle.name;
  } catch {
    return null; // cancelled the picker
  }
}

export async function forgetSaveFolder() {
  try { await idbDel(KEY); } catch { /* nothing stored */ }
}

/**
 * The stored root, or null. `prompt` decides what happens when permission has
 * lapsed: true re-asks (only legal from a click), false gives up quietly.
 */
export async function getSaveFolder({ prompt = false } = {}) {
  if (!isFolderSaveSupported()) return null;
  let handle = null;
  try { handle = await idbGet(KEY); } catch { return null; }
  if (!handle) return null;
  try {
    const opts = { mode: "readwrite" };
    if ((await handle.queryPermission(opts)) === "granted") return handle;
    if (!prompt) return null;
    if ((await handle.requestPermission(opts)) === "granted") return handle;
    return null;
  } catch {
    return null;
  }
}

/** Name of the stored folder without asking for anything. */
export async function savedFolderName() {
  if (!isFolderSaveSupported()) return null;
  try {
    const handle = await idbGet(KEY);
    return handle ? handle.name : null;
  } catch {
    return null;
  }
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// Strip anything Windows won't accept in a folder name, and the trailing
// dots/spaces it silently drops. A broker name is owner-typed, so this is
// about not failing on a stray "/" rather than about hostile input.
function safeSegment(name) {
  return String(name || "")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/, "")
    .trim();
}

/**
 * Folder names for a job, under the granted root: broker, then year, then
 * month. Dates come from the job's SCHEDULED date, not today — billing a
 * September job in October still files it under September 2026. Falls back
 * to today only when the job has no date.
 *
 * brokerFolder is the rate card's "save folder" name. Broker NAMES don't
 * always match the folders on disk ("Malca Amit" vs a folder called
 * "Malca"), so deriving it from the name would build a second, wrong
 * folder alongside the real one. Blank falls back to the broker name, and
 * a blank result drops the level entirely rather than creating "".
 */
export function folderPathForJob(job, brokerFolder, dateFolders = true) {
  const broker = safeSegment(brokerFolder || job?.brokerName);
  // Low-volume brokers file flat — a year and month folder per job would be
  // more folders than files. Opt out per broker on the rate card.
  if (!dateFolders) return broker ? [broker] : [];
  const ymd = String(job?.scheduledDate || "");
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date();
  return [
    ...(broker ? [broker] : []),
    String(d.getFullYear()),
    `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
  ];
}

/**
 * Write one file into <root>/<year>/<Month year>/, creating the folders as
 * needed. Returns the path written, or null so the caller can fall back to a
 * normal download.
 */
export async function saveFileToFolder(root, job, filename, blob, opts = {}) {
  if (!root || !blob) return null;
  try {
    const parts = folderPathForJob(job, opts.brokerFolder, opts.dateFolders !== false);
    let dir = root;
    for (const part of parts) {
      // eslint-disable-next-line no-await-in-loop
      dir = await dir.getDirectoryHandle(part, { create: true });
    }
    const file = await dir.getFileHandle(filename, { create: true });
    const w = await file.createWritable();
    await w.write(blob);
    await w.close();
    return `${root.name}/${parts.join("/")}/${filename}`;
  } catch (err) {
    console.warn("Couldn't write into the save folder", err);
    return null;
  }
}
